import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ASSETS } from '../BaseURL';
import { subscribeCart, readLastSnapshot } from '../utils/customerDisplay';
import { fmtMoney } from '../utils/format';
import '../styles/customer-display.css';

// The customer-facing display window. Opened by the POS via
//   window.open('/customer-display', 'cfd', 'width=1280,height=800')
// then the cashier drags it to the rear monitor and presses F11 for fullscreen.
//
// It listens on a BroadcastChannel for cart snapshots and paints a clean
// customer-friendly view — no sidebar, no auth, no controls. The only
// interactive touch is a fullscreen toggle button that hides itself when
// the pointer is idle.
const CustomerDisplay = () => {
  const [state, setState] = useState(() => readLastSnapshot() || { kind: 'idle' });
  const [now, setNow] = useState(() => new Date());
  const [fs, setFs] = useState(false);
  const listRef = useRef(null);
  const hideCtrlTimer = useRef(null);
  const [ctrlHidden, setCtrlHidden] = useState(true);

  // Subscribe once — no re-subscribes needed.
  useEffect(() => {
    const unsub = subscribeCart((payload) => setState(payload || { kind: 'idle' }));
    return unsub;
  }, []);

  // Live wall clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(t);
  }, []);

  // Auto-hide the fullscreen button when the cashier isn't hovering.
  useEffect(() => {
    const onMove = () => {
      setCtrlHidden(false);
      if (hideCtrlTimer.current) clearTimeout(hideCtrlTimer.current);
      hideCtrlTimer.current = setTimeout(() => setCtrlHidden(true), 2500);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // Auto-scroll the item list so the newest scan is always visible.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [state?.items?.length]);

  // Auto-return to idle 6 seconds after a thank-you payload.
  useEffect(() => {
    if (state?.kind !== 'thank-you') return undefined;
    const t = setTimeout(() => setState({ kind: 'idle' }), 6000);
    return () => clearTimeout(t);
  }, [state]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setFs(true);
      } else {
        await document.exitFullscreen();
        setFs(false);
      }
    } catch (_) {}
  };

  const facilityLogo = state?.facility?.logo ? `${ASSETS}${state.facility.logo}` : null;
  const facilityName = state?.facility?.name || 'Point of Sale';
  const branchName   = state?.branchName || '';

  const timeStr = useMemo(() =>
    now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  [now]);
  const dateStr = useMemo(() =>
    now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }),
  [now]);

  return (
    <div className={`cfd ${state?.kind || 'idle'}`}>
      <button
        className={`cfd-fs ${ctrlHidden ? 'hidden' : ''}`}
        onClick={toggleFullscreen}
        title={fs ? 'Exit fullscreen' : 'Enter fullscreen'}
      >{fs ? '⤢ Exit fullscreen' : '⤢ Fullscreen'}</button>

      <header className="cfd-head">
        <div className="brand">
          {facilityLogo && <img src={facilityLogo} alt={facilityName} />}
          <div>
            <div className="nm">{facilityName}</div>
            {branchName && <div className="sub">{branchName}</div>}
          </div>
        </div>
        <div className="clock">
          <div className="time">{timeStr}</div>
          <div className="date">{dateStr}</div>
        </div>
      </header>

      {state?.kind === 'thank-you' && (
        <div className="cfd-thanks">
          <div className="tick">✓</div>
          <h1>Thank you for shopping!</h1>
          <p>Please collect your receipt.</p>
          {state.receiptNo && <div className="rcpt">Receipt #{state.receiptNo}</div>}
          <div className="thanks-total">
            <div><span>Paid</span><strong>{fmtMoney(state.total)}</strong></div>
            {Number(state.change) > 0 && (
              <div><span>Change</span><strong>{fmtMoney(state.change)}</strong></div>
            )}
          </div>
        </div>
      )}

      {state?.kind === 'idle' && (
        <div className="cfd-idle">
          <div className="pulse">🛒</div>
          <h1>Welcome</h1>
          <p>Please wait while your items are being scanned.</p>
        </div>
      )}

      {state?.kind === 'cart' && (
        <>
          <div className="cfd-items" ref={listRef}>
            {(state.items || []).map((it, i) => (
              <div key={`${it.product_id}-${i}`} className={`cfd-line ${i === state.items.length - 1 ? 'new' : ''}`}>
                <div className="qty">×{it.qty}</div>
                <div className="nm">
                  <div className="name">{it.name}</div>
                  <div className="rate">{fmtMoney(it.unit_price)} {it.unit ? `/ ${it.unit}` : ''}</div>
                </div>
                <div className="lt">{fmtMoney(it.line_total)}</div>
              </div>
            ))}
          </div>

          <footer className="cfd-foot">
            <div className="totals">
              <div className="row"><span>Subtotal</span><span>{fmtMoney(state.subtotal)}</span></div>
              {Number(state.discount) > 0 && (
                <div className="row discount"><span>Discount</span><span>− {fmtMoney(state.discount)}</span></div>
              )}
              <div className="row grand"><span>TOTAL</span><span>{fmtMoney(state.total)}</span></div>
              {Number(state.paid) > 0 && (
                <>
                  <div className="row small"><span>Paid so far</span><span>{fmtMoney(state.paid)}</span></div>
                  {Number(state.due) > 0
                    ? <div className="row due"><span>Amount due</span><span>{fmtMoney(state.due)}</span></div>
                    : Number(state.change) > 0 && (
                      <div className="row change"><span>Change</span><span>{fmtMoney(state.change)}</span></div>
                    )
                  }
                </>
              )}
            </div>
            <div className="cart-count">{state.cartCount} {state.cartCount === 1 ? 'item' : 'items'}</div>
          </footer>
        </>
      )}
    </div>
  );
};

export default CustomerDisplay;
